import type { TextString, TextStyle, TextStyleOptions } from 'pixi.js';
import { Text } from 'pixi.js';
import type { LayoutHTMLText } from './LayoutHTMLText';

export type LayoutTextProcessor = (textObject: LayoutText | LayoutHTMLText) => void;

export interface LayoutTextSize {
  maxWidth?: number;
  maxHeight?: number;
  maxFontSize: number;
}

/**
 * Extension of Pixi.Text which adds methods for enforcing maxHeight / maxWidth.
 */
export class LayoutText extends Text {
  public static textProcessors: LayoutTextProcessor[] = [];

  private sizeRestrictions?: LayoutTextSize;

  set text(value: TextString) {
    super.text = value;
    LayoutText.processText(this);
    this.enforceSizeRestrictions();
  }

  get text(): string {
    return super.text;
  }

  public static processText(textObject: LayoutText | LayoutHTMLText): void {
    LayoutText.textProcessors.forEach((processor) => {
      processor(textObject);
    });
  }

  set style(value: TextStyle | Partial<TextStyle> | TextStyleOptions) {
    super.style = value;
    this.enforceSizeRestrictions();
  }

  get style(): TextStyle {
    return super.style;
  }

  public setTextSizeRestrictions(sizeRestrictions: LayoutTextSize): void {
    this.sizeRestrictions = sizeRestrictions;
    this.enforceSizeRestrictions();
  }

  private enforceSizeRestrictions(): void {
    if (this.destroyed || !this.sizeRestrictions) return;
    const { maxWidth, maxHeight, maxFontSize } = this.sizeRestrictions;

    try {
      this.style.fontSize = maxFontSize;
      while (this.style.fontSize > 1 && ((maxWidth && this.width > maxWidth) || (maxHeight && this.height > maxHeight))) {
        this.style.fontSize -= 1;
      }
    } catch (e) {
      // Measuring text (Pixi CanvasTextMetrics) can throw when a webfont fails to decode
      // (e.g. a publisher-injected Roboto returning an invalid sfntVersion). Never let that
      // propagate out of layout population and freeze the game — skip auto-sizing this text.
      console.warn('LayoutText.enforceSizeRestrictions: text measurement failed', e);
    }
  }
}
